/**
 * "The request was never answered" — cancelled by us, or lost in transport
 * (offline, flaky wifi, a socket the OS killed while the app was backgrounded).
 * Distinct from "the server said no": a non-2xx response is a COMPLETED
 * request and stays a real failure.
 *
 * Both clients are offline-first, so an unanswered request is expected flow,
 * not an error to report — reporting it was ~630k Sentry events / 67k users on
 * mobile alone (FLASHKA-APP-2G). One classifier, by the SHAPE of the error,
 * for every transport either client uses: axios, browser `fetch`, React
 * Native's `fetch` (whatwg-fetch over XHR) and `expo/fetch`.
 *
 * NOT unanswered (still reported): timeouts — the server took 90s and never
 * answered is a server signal — TLS / certificate / ATS failures, bad URLs, a
 * server that refused the connection, and anything that carries a response.
 */

import {
  IOS_CANCELLED_DESCRIPTIONS,
  IOS_UNANSWERED_DESCRIPTIONS,
} from './unanswered-request-ios-descriptions';

/**
 * Matched by `name`, NOT `instanceof DOMException`/`Error`: an abort crosses
 * realms routinely — a jest vm sandbox, an iframe, a worker — and `instanceof`
 * is false across every one of them while `name` survives intact.
 */
export const isAbortError = (err: unknown): boolean =>
  typeof err === 'object' &&
  err !== null &&
  (err as { name?: unknown }).name === 'AbortError';

/**
 * `fetch` transport failures are `TypeError`s with one of a few well-known
 * messages. Matched by message — NOT `instanceof TypeError` alone — so genuine
 * code bugs that happen to be TypeErrors ("cannot read properties of
 * undefined") are not mistaken for a transport failure.
 */
const NETWORK_ERROR_MESSAGES = [
  'Failed to fetch', // Chrome
  'NetworkError when attempting to fetch resource.', // Firefox
  'Load failed', // Safari
  'fetch failed', // undici
  'Network request failed', // React Native (whatwg-fetch)
];

/**
 * A lazy chunk that failed to load is a deploy-skew / stale-bundle problem the
 * web app recovers from on its own (`handlePreloadError`), and it is tracked as
 * its own issue — it must not disappear into "offline".
 */
const CHUNK_LOAD_MARKER = 'dynamically imported module';

const BODY_STREAM_FAILURES = [
  'network error', // Chrome, Edge
  'Error in input stream', // Firefox
];

/**
 * `expo/fetch` wraps EVERY native pre-response failure in a `FetchError` whose
 * message is prefixed with this — offline, but also a timeout, a TLS trust
 * failure, an ATS block, a malformed URL. So the prefix alone says nothing;
 * what follows it decides, and only a known offline / lost / cancelled
 * description counts as unanswered. Anything unrecognised stays reported.
 */
const EXPO_FETCH_ERROR_PREFIX = 'fetch failed: ';

const normaliseDescription = (text: string): string =>
  text.toLowerCase().replace(/\s+/g, ' ');

/**
 * OkHttp's IOException messages for a dead or lost connection (never
 * localized). UnknownHostException — "Unable to resolve host" — is what
 * Android reports for "no network at all".
 */
const ANDROID_UNANSWERED_MESSAGES = [
  'Unable to resolve host',
  'Network is unreachable', // ENETUNREACH
  'Connection reset',
  // POSIX, both OSes — a socket the OS killed while the app was backgrounded.
  'Software caused connection abort',
  'Socket is not connected',
];

const EXPO_FETCH_UNANSWERED_DESCRIPTIONS = [
  // expo/fetch's own: a cancelled native request, and an aborted signal.
  'Fetch request has been canceled',
  'The operation was aborted.',
  'java.io.IOException: Canceled', // OkHttp's own cancel
  'java.net.UnknownHostException', // as `toString()`, whatever its message
  ...ANDROID_UNANSWERED_MESSAGES,
  // iOS: NSURLErrorDomain's localized text, in every language the OS ships.
  ...IOS_UNANSWERED_DESCRIPTIONS,
].map(normaliseDescription);

const includesAny = (text: string, needles: readonly string[]): boolean => {
  const detail = normaliseDescription(text);
  return needles.some((d) => detail.includes(d));
};

// iOS's "cancelled", in every language — matched only as the whole description.
const IOS_CANCELLED_NEEDLES = new Set(
  IOS_CANCELLED_DESCRIPTIONS.map((d) => normaliseDescription(d).trim()),
);

/** `text` IS a native cancellation, once expo's `UnexpectedException: … (at …)`
 *  wrapper is peeled off — not a longer message that merely contains the word. */
const isBareIosCancellation = (text: string): boolean =>
  IOS_CANCELLED_NEEDLES.has(
    normaliseDescription(text)
      .trim()
      .replace(/^unexpectedexception: /, '')
      .replace(/ \(at [^)]*\)$/, '')
      .trim(),
  );

const isUnansweredExpoFetchFailure = (message: string): boolean =>
  message.startsWith(EXPO_FETCH_ERROR_PREFIX) &&
  (includesAny(
    message.slice(EXPO_FETCH_ERROR_PREFIX.length),
    EXPO_FETCH_UNANSWERED_DESCRIPTIONS,
  ) ||
    isBareIosCancellation(message.slice(EXPO_FETCH_ERROR_PREFIX.length)));

/**
 * `text` carries a native lost / offline / cancelled description (iOS, in any
 * OS language, or OkHttp's). Besides the `fetch failed: ` prefix above,
 * `expo/fetch` errors a response BODY that drops mid-download with a plain
 * `Error` whose whole message is that description — no prefix, no code — so a
 * caller classifying a mid-stream failure asks this directly.
 */
export const isUnansweredTransportDescription = (text: string): boolean =>
  includesAny(text, EXPO_FETCH_UNANSWERED_DESCRIPTIONS) || isBareIosCancellation(text);

const ANDROID_UNANSWERED_NEEDLES = ANDROID_UNANSWERED_MESSAGES.map(normaliseDescription);

/** The NSError domain + code of an unanswered request; never localized. */
const UNANSWERED_NS_ERRORS = new Set([
  'NSURLErrorDomain -999', // cancelled
  'NSURLErrorDomain -1003', // cannot find host
  'NSURLErrorDomain -1005', // connection lost
  'NSURLErrorDomain -1009', // offline
  'NSURLErrorDomain -1020', // data not allowed
  'NSPOSIXErrorDomain 53', // ECONNABORTED
  'NSPOSIXErrorDomain 57', // ENOTCONN
]);

/**
 * A native task's coded rejection — expo-file-system's upload task. iOS puts
 * the NSError's `description` in the message (`Unable to upload the file:
 * 'Error Domain=NSURLErrorDomain Code=-1009 "…"'`), so its OUTERMOST domain +
 * code decide; Android puts OkHttp's IOException message there, unprefixed.
 */
const isUnansweredNativeTaskFailure = (err: Error): boolean => {
  if (typeof (err as { code?: unknown }).code !== 'string') return false;
  const ns = /Error Domain=(\S+) Code=(-?\d+)/.exec(err.message);
  if (ns) return UNANSWERED_NS_ERRORS.has(`${ns[1]} ${ns[2]}`);
  return includesAny(err.message, ANDROID_UNANSWERED_NEEDLES);
};

/** The request never completed at the transport level. */
export const isNetworkFailure = (err: unknown): boolean => {
  if (!(err instanceof Error)) return false;
  const { message } = err;
  if (err instanceof TypeError) {
    if (message.includes(CHUNK_LOAD_MARKER)) return false;
    // A body read (`res.json()`, a streaming reader) rejects with exactly
    // these when the connection drops mid-download — Chrome/Edge (net::
    // ERR_HTTP2_PROTOCOL_ERROR, ERR_INCOMPLETE_CHUNKED_ENCODING) and Firefox.
    // The transport died; the response never finished (FLASHKA-VITE-QD,
    // -2EZ). Exact match: the words are too generic to look for inside a
    // longer message.
    if (BODY_STREAM_FAILURES.includes(message)) return true;
    return NETWORK_ERROR_MESSAGES.some((m) => message.includes(m));
  }
  // `expo/fetch` only REJECTS when no response arrived: once the native request
  // starts, HTTP errors resolve as a Response.
  return isUnansweredExpoFetchFailure(message) || isUnansweredNativeTaskFailure(err);
};

type AxiosLike = {
  isAxiosError?: unknown;
  code?: unknown;
  message?: unknown;
  response?: unknown;
  __CANCEL__?: unknown;
};

/** True only on a browser that says it is online; unknown (RN, node) is false. */
const browserSaysOnline = (): boolean =>
  typeof navigator !== 'undefined' && (navigator as { onLine?: unknown }).onLine === true;

/**
 * Axios errors, matched by shape so neither client needs axios to ask:
 *   - `ERR_CANCELED` / `__CANCEL__` — an AbortSignal or CancelToken we own;
 *   - `ERR_NETWORK` with no response — XHR `onerror`, i.e. offline / lost.
 *     XHR gives `onerror` no detail at all, so a TLS or DNS failure looks the
 *     same. Where the platform can say otherwise — a browser whose
 *     `navigator.onLine` is still true — it stays reported. React Native has
 *     no `navigator.onLine` (and `shared/` has no NetInfo), so there every
 *     `ERR_NETWORK` is still treated as offline: the one blind spot left;
 *   - `ECONNABORTED` "Request aborted" — XHR `onabort`. Axios uses the SAME
 *     code for a timeout ("timeout of 90000ms exceeded"), which is reported,
 *     so the message is what tells them apart.
 */
export const isUnansweredAxiosRequest = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as AxiosLike;
  if (e.__CANCEL__ === true || e.code === 'ERR_CANCELED') return true;
  if (e.isAxiosError !== true || e.response != null) return false;
  if (e.code === 'ERR_NETWORK') return !browserSaysOnline();
  return e.code === 'ECONNABORTED' && e.message === 'Request aborted';
};

/**
 * The request was never answered — cancelled or lost in transport. Pass the
 * error a transport threw, or `hint.originalException` in Sentry's
 * `beforeSend`.
 */
export const isUnansweredRequest = (err: unknown): boolean =>
  isAbortError(err) || isUnansweredAxiosRequest(err) || isNetworkFailure(err);

/**
 * Sentry `beforeSend`, shared by both clients: drop an event whose original
 * exception is an unanswered request, whoever captured it. Typed structurally
 * so `shared/` takes no Sentry dependency. Breadcrumbs are unaffected — the
 * failed request still appears in the trail of any later, real error.
 */
export function dropUnansweredRequestEvent<E>(
  event: E,
  hint: { originalException?: unknown } | undefined,
): E | null {
  return isUnansweredRequest(hint?.originalException) ? null : event;
}
